/**
 * Return URL preservation: persist and resolve the URL to redirect to after login.
 * Uses localStorage so the intended URL survives refresh and session-expiry flows.
 */

export const RETURN_URL_STORAGE_KEY = 'returnUrl';

/** sessionStorage key: set during manual logout so ProtectedRoute does not overwrite cleared returnUrl (avoids race). */
const LOGGING_OUT_KEY = 'returnUrl_loggingOut';

export const setLoggingOut = () => {
  try {
    sessionStorage.setItem(LOGGING_OUT_KEY, '1');
  } catch (_) {}
};

export const isLoggingOut = () => {
  try {
    return sessionStorage.getItem(LOGGING_OUT_KEY) === '1';
  } catch (_) {
    return false;
  }
};

export const clearLoggingOut = () => {
  try {
    sessionStorage.removeItem(LOGGING_OUT_KEY);
  } catch (_) {}
};

/** Paths we never redirect to after login (avoid loops). */
const BLOCKED_PATHS = ['/login', '/forgotPassword'];

/**
 * Returns true if the path is safe to use as a post-login redirect (same-origin, relative, not login/forgot).
 * @param {string} path - Path to validate (e.g. pathname + search).
 * @returns {boolean}
 */
export const isValidReturnPath = (path) => {
  if (!path || typeof path !== 'string') return false;
  const trimmed = path.trim();
  if (trimmed.length === 0) return false;
  // Must be relative (same-origin), no protocol or // 

  if (!trimmed.startsWith('/')) return false;
  const basePath = trimmed.split('?')[0];
  if (BLOCKED_PATHS.includes(basePath)) return false;
  return true;
};

/**
 * Persist the current full path (pathname + search) so the user can be redirected after login.
 * @param {string} path - Full path to save (e.g. location.pathname + location.search).
 */
export const saveReturnUrl = (path) => {
  if (!path || !isValidReturnPath(path)) return;
  try {
    localStorage.setItem(RETURN_URL_STORAGE_KEY, path);
  } catch (e) {
    console.warn('returnUrl: could not save to localStorage', e);
  }
};

/**
 * Remove the stored return URL (e.g. on manual logout so next login goes to home).
 */
export const clearReturnUrl = () => {
  try {
    localStorage.removeItem(RETURN_URL_STORAGE_KEY);
  } catch (_) {}
};

/**
 * Get the return URL from location state, search params, or localStorage (in that order).
 * Clears localStorage entry when read so it is only used once.
 * @param {object} location - React Router location (pathname, search, state).
 * @param {URLSearchParams} [searchParams] - Optional search params (e.g. from useSearchParams()).
 * @param {{ clearStorage?: boolean }} [options] - If clearStorage is true (default), removes from localStorage after read.
 * @returns {string|null} Valid return path or null (caller should use '/' as default).
 */
export const getReturnUrl = (location, searchParams, options = {}) => {
  const { clearStorage = true } = options;

  // 1. Router state (from ProtectedRoute <Navigate state={{ from: location }} />)
  const from = location?.state?.from;
  if (from) {
    const path = (from.pathname || '') + (from.search || '');
    if (isValidReturnPath(path)) {
      if (clearStorage) try { localStorage.removeItem(RETURN_URL_STORAGE_KEY); } catch (_) {}
      return path;
    }
  }

  // 2. Query param ?returnUrl=...
  if (searchParams) {
    const queryUrl = searchParams.get('returnUrl');
    if (queryUrl && isValidReturnPath(queryUrl)) {
      if (clearStorage) try { localStorage.removeItem(RETURN_URL_STORAGE_KEY); } catch (_) {}
      return queryUrl;
    }
  }

  // 3. localStorage (persisted by ProtectedRoute or ExpiredSessionDialog)
  try {
    const stored = localStorage.getItem(RETURN_URL_STORAGE_KEY);
    if (stored && isValidReturnPath(stored)) {
      if (clearStorage) localStorage.removeItem(RETURN_URL_STORAGE_KEY);
      return stored;
    }
  } catch (_) {}

  return null;
};
